'use client'

import { useEffect } from 'react'
import { PrimaryButton, SecondaryButton } from '@/src/components/Buttons'

const CAL_EMBED_SRC = 'https://app.cal.com/embed/embed.js'
const CAL_NAMESPACE = '30min'
const CAL_LINK = 'george-witn/30min'
const CAL_CONFIG =
  '{"layout":"week_view","useSlotsViewOnSmallScreen":"true","theme":"light"}'

type CalApi = ((...args: unknown[]) => void) & {
  q?: unknown[]
  ns?: Record<string, CalApi>
}

declare global {
  interface Window {
    Cal?: CalApi
  }
}

let calInitialized = false

function createQueuedApi(): CalApi {
  const api = ((...args: unknown[]) => {
    api.q = api.q || []
    api.q.push(args)
  }) as CalApi
  api.q = []
  return api
}

function ensureCalShim() {
  if (window.Cal) return

  const cal = ((...args: unknown[]) => {
    if (args[0] === 'init') {
      const namespace = args[1]

      if (typeof namespace === 'string') {
        cal.ns = cal.ns || {}
        cal.ns[namespace] = cal.ns[namespace] || createQueuedApi()
        cal.ns[namespace](...args)
        cal.q = cal.q || []
        cal.q.push(['initNamespace', namespace])
        return
      }
    }

    cal.q = cal.q || []
    cal.q.push(args)
  }) as CalApi

  cal.q = []
  cal.ns = {}
  window.Cal = cal
}

function loadCalScript() {
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${CAL_EMBED_SRC}"]`,
  )
  if (existing) return

  const script = document.createElement('script')
  script.async = true
  script.src = CAL_EMBED_SRC
  document.head.appendChild(script)
}

function initCal() {
  if (calInitialized) return

  ensureCalShim()
  loadCalScript()

  window.Cal?.('init', CAL_NAMESPACE, { origin: 'https://app.cal.com' })
  window.Cal?.ns?.[CAL_NAMESPACE]?.('ui', {
    theme: 'light',
    hideEventTypeDetails: false,
    layout: 'week_view',
  })

  calInitialized = true
}

interface BookDemoButtonProps {
  label?: string
  source?: string
  color?: 'primary' | 'secondary'
}

const BookDemoButton: React.FC<BookDemoButtonProps> = ({
  label = 'Book a demo',
  source,
  color = 'primary',
}) => {
  useEffect(() => {
    initCal()
  }, [])

  const Button = color === 'secondary' ? SecondaryButton : PrimaryButton
  return (
    <Button
      data-cal-namespace={CAL_NAMESPACE}
      data-cal-link={CAL_LINK}
      data-cal-config={CAL_CONFIG}
      data-demo-source={source}
    >
      {label}
    </Button>
  )
}

export default BookDemoButton
